import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  DatePicker,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import Swal from "sweetalert2";
import { Button, Icon } from "../../../../components/Component";
import {
  deploymentNormApis,
  DeploymentNormLinePayload,
  SaveDeploymentNormVersionPayload,
} from "../../../../api/MachineryEquipment";
import { assetApis } from "../../../../api/Assets";
import adminOrgApis from "../../../../api/master/adminOrg";
import { statusTagColor, statusLabel } from "../../assets/AssetStatusModal";
import RateUnitToggle from "../../shared/RateUnitToggle";
import { moneyPerUnit, useRateUnit } from "../../shared/useRateUnit";
import {
  computeDeploymentHours,
  computeHoursPerUnit,
  formatHours,
} from "./deploymentNormHours";

/** ₹/hr display — decimals arrive as strings, blanks render as a dash. */
const money = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Deployment Norm editor — ONE norm code's full equipment matrix, VERSIONED.
 *
 * The matrix is the grain: saving never patches a line, it INSERTS a complete
 * new version (all lines) effective from a chosen instant — same model as the
 * Equipment Rate editor. The form loads the version in force now; only the
 * History drawer can undo (latest version only).
 *
 * Equipment comes from the Asset Master picker feed; a machine can appear
 * once per norm. Prod. Norm 0 keeps the machine in the matrix as "considered,
 * not deployed" — the row is stored and exported, it just earns no hours.
 *
 * The what-if Qty column is DISPLAY ONLY — a client-side mirror of the
 * backend's computeTotalWorkingHours (see deploymentNormHours.ts). Nothing
 * computed here is ever sent.
 *
 * Factors are FRACTIONS end to end (0.05 = 5%): the API stores fractions and
 * this form edits them as fractions — no percent conversion boundary, unlike
 * the Equipment Rate editor.
 */

interface LineRow {
  /** Local render key — new rows have no server id. */
  _key: number;
  versionEquipmentId?: number;
  assetId: number | null;
  nonProductionHoursFactor: number | null;
  effectiveUtilizationFactor: number | null;
  applicationFactor: number | null;
  uomId: number | null;
  prodNorm: number | null;
}

const num = (value: any): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// Factors are stored/sent as FRACTIONS but EDITED as whole-number percents —
// the same convention (and float-dust rounding) as the Equipment Rate editor.
const toPct = (frac: number | null): number | null =>
  frac === null ? null : Math.round(frac * 100 * 1e6) / 1e6;
const fromPct = (value: number | null | undefined): number | null =>
  value === null || value === undefined ? null : Math.round((Number(value) / 100) * 1e8) / 1e8;

const DeploymentNormEditor = ({
  deploymentNormId,
  projectId = 0,
  projectName = "",
  onClose,
  onSaved,
}: {
  /** null = create a new norm (header + first version in one save). */
  deploymentNormId: number | null;
  projectId?: number;
  /** Display name for the scope tag — "Trumpet HEP", never a raw id. */
  projectName?: string;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const isCreate = !deploymentNormId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Header fields + the loaded baseline (to detect a rename needing
  // UpdateDeploymentNorm alongside the version save).
  const [normCode, setNormCode] = useState("");
  const [description, setDescription] = useState("");
  const [baseHeader, setBaseHeader] = useState<any>(null);
  const [versionCount, setVersionCount] = useState(0);
  /** The in-force version's effectiveFrom (null = no version yet). */
  const [inForceFrom, setInForceFrom] = useState<string | null>(null);

  const [lines, setLines] = useState<LineRow[]>([]);
  const [nextKey, setNextKey] = useState(1);

  /** What-if output quantity for the derived Hours column. */
  const [whatIfQty, setWhatIfQty] = useState<number | null>(1000);

  // Asset picker feed ({assetId, assetCode, assetName, toolTypeName, status,
  // capacity}) — excludes Retired machines.
  const [assets, setAssets] = useState<any[]>([]);
  const [uomOptions, setUomOptions] = useState<
    Array<{ value: number; label: string }>
  >([]);

  /**
   * assetId -> ₹/hr, from the fetch-one response. Populated once on load; the
   * FACTORS are what the user edits here, never the rates, so this map stays
   * valid for the whole editing session.
   */
  const [rateByAsset, setRateByAsset] = useState<Map<number, number>>(new Map());

  const [rateUnit] = useRateUnit();
  const showPerUnit = rateUnit === "OUTPUT";
  /** Blank unless the whole matrix measures output in one unit. */
  const matrixUom = useMemo(() => {
    const labels = lines
      .map(
        (line) =>
          uomOptions.find((opt) => Number(opt.value) === Number(line.uomId))
            ?.label
      )
      .filter((label): label is string => Boolean(label))
      .map((label) => label.split(" (")[0])
      .filter((label, index, all) => all.indexOf(label) === index);
    return labels.length === 1 ? labels[0] : "";
  }, [lines, uomOptions]);
  const rateSuffix = showPerUnit ? `₹/${matrixUom || "unit"}` : "₹/hr";

  // The save-as-version modal: WHEN the new matrix takes effect, and why.
  const [saveOpen, setSaveOpen] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs>(dayjs());
  const [remarks, setRemarks] = useState("");

  const defaultUomId = useMemo(() => {
    const cum = uomOptions.find((opt) => /\bcum\b/i.test(String(opt.label)));
    return cum ? Number(cum.value) : null;
  }, [uomOptions]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assetsRes, uomRes, normRes] = await Promise.all([
        assetApis.getOptions(),
        adminOrgApis.fetchUom(),
        deploymentNormId
          ? deploymentNormApis.getOne(deploymentNormId)
          : Promise.resolve(null),
      ]);

      setAssets(assetsRes?.data?.items || []);

      // Same UOM mapping as MachineryNormModal: value workUomId??uomId??id,
      // label "name (shortCode)".
      const uomData = uomRes?.data;
      if (uomRes?.status && Array.isArray(uomData)) {
        setUomOptions(
          uomData.map((item: any) => ({
            value: Number(item?.workUomId ?? item?.uomId ?? item?.id),
            label: item?.uomShortCode
              ? `${item?.uomName} (${item?.uomShortCode})`
              : item?.uomName,
          }))
        );
      }

      if (deploymentNormId) {
        if (!normRes?.status) {
          message.error("Could not load this equipment norm.");
          return;
        }
        // Tolerate both flat and nested fetch-one shapes: header fields at the
        // top level (or under .norm), the in-force version under .version (or
        // flat effectiveFrom/lines).
        const data = normRes?.data || {};
        const header = data.norm ?? data;
        const version = data.version ?? null;
        const versionLines: any[] = version?.lines ?? data.lines ?? [];

        setBaseHeader(header);
        setNormCode(header?.normCode || "");
        setDescription(header?.description || "");
        setVersionCount(Number(header?.versionCount ?? data.versionCount) || 0);
        setInForceFrom(version?.effectiveFrom ?? data.effectiveFrom ?? null);
        setLines(
          versionLines.map((line: any, index: number) => ({
            _key: index + 1,
            versionEquipmentId:
              line.versionEquipmentId,
            assetId: num(line.assetId),
            nonProductionHoursFactor: num(line.nonProductionHoursFactor),
            effectiveUtilizationFactor: num(line.effectiveUtilizationFactor),
            applicationFactor: num(line.applicationFactor),
            uomId: num(line.uomId),
            prodNorm: num(line.prodNorm),
          }))
        );
        setNextKey(versionLines.length + 1);
        // The lines arrive PRICED (fetch-one resolves rates as-of now), so the
        // cost column has numbers before the user touches anything.
        setRateByAsset(
          new Map(
            versionLines
              .filter((line: any) => line.hourlyRate !== undefined && line.hourlyRate !== null)
              .map((line: any) => [Number(line.assetId), Number(line.hourlyRate)])
          )
        );
        // Merge line-carried UOMs the org list doesn't offer (the backend may
        // store the GLOBAL uom_master row — e.g. "Cubit Meter" id 3 — while
        // /admin/fetchUom returns the org-scoped list, e.g. "Cum" id 213), so
        // the Unit select never falls back to rendering a raw id.
        setUomOptions((prev) => {
          const known = new Set(prev.map((opt) => Number(opt.value)));
          const extras = versionLines
            .filter(
              (l: any) => l.uomId && l.uomName && !known.has(Number(l.uomId))
            )
            .map((l: any) => ({
              value: Number(l.uomId),
              label: String(l.uomName),
            }))
            .filter(
              (opt: any, index: number, all: any[]) =>
                all.findIndex((other) => other.value === opt.value) === index
            );
          return extras.length ? [...prev, ...extras] : prev;
        });
        // Merge line-carried assets the picker feed no longer offers (e.g. a
        // machine retired since this version), so the row still renders.
        setAssets((prev) => {
          const known = new Set(prev.map((a: any) => Number(a.assetId)));
          const extras = versionLines
            .filter((l: any) => l.assetId && !known.has(Number(l.assetId)))
            .map((l: any) => ({
              assetId: Number(l.assetId),
              assetCode: l.assetCode,
              assetName: l.assetName,
              toolTypeName: l.toolTypeName,
              status: l.assetStatus,
              capacity: l.capacity,
            }));
          return extras.length ? [...prev, ...extras] : prev;
        });
      }
    } finally {
      setLoading(false);
    }
  }, [deploymentNormId]);

  useEffect(() => {
    load();
  }, [load]);

  const setLine = (key: number, patch: Partial<LineRow>) =>
    setLines((prev) =>
      prev.map((line) => (line._key === key ? { ...line, ...patch } : line))
    );

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        _key: nextKey,
        assetId: null,
        nonProductionHoursFactor: null,
        effectiveUtilizationFactor: null,
        applicationFactor: null,
        uomId: defaultUomId,
        prodNorm: null,
      },
    ]);
    setNextKey((k) => k + 1);
  };

  const removeLine = (key: number) =>
    setLines((prev) => prev.filter((line) => line._key !== key));

  /** Asset options for one row: everything not already picked by ANOTHER row. */
  const optionsForRow = useCallback(
    (row: LineRow) => {
      const taken = new Set(
        lines
          .filter((l) => l._key !== row._key && l.assetId)
          .map((l) => Number(l.assetId))
      );
      return assets
        .filter((asset: any) => !taken.has(Number(asset.assetId)))
        .map((asset: any) => ({
          value: Number(asset.assetId),
          searchText: `${asset.assetCode || ""} ${asset.assetName || ""}`.toLowerCase(),
          label: (
            <span>
              {asset.assetCode} — {asset.assetName}
              <Tag
                color={statusTagColor(asset.status)}
                style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}
              >
                {statusLabel(asset.status)}
              </Tag>
            </span>
          ),
        }));
    },
    [assets, lines]
  );

  const validate = (): string | null => {
    if (!normCode.trim()) return "Enter a Norm Code.";
    if (!lines.length)
      return "Add at least one equipment line — a version is the whole matrix.";
    if (lines.some((line) => !line.assetId))
      return "Every line needs an equipment picked (or removed).";
    const ids = lines.map((line) => Number(line.assetId));
    if (new Set(ids).size !== ids.length)
      return "The same equipment appears more than once — each machine can appear only once per norm.";
    const badEuf = lines.some((line) => {
      const euf = num(line.effectiveUtilizationFactor);
      return euf !== null && euf <= 0;
    });
    if (badEuf)
      return "Effective Utilization Factor must be greater than 0 — the hours formula divides by it.";
    return null;
  };

  const openSaveModal = () => {
    const error = validate();
    if (error) {
      message.error(error);
      return;
    }
    setEffectiveFrom(dayjs());
    setRemarks("");
    setSaveOpen(true);
  };

  const handleSaveVersion = async () => {
    if (!effectiveFrom) {
      message.error("Pick when this version takes effect.");
      return;
    }
    setSaving(true);
    try {
      // A rename / description change on an EXISTING norm goes through the
      // header-edit endpoint — SaveVersion resolves the header, it does not
      // rewrite it. The header edit runs AFTER the version save so a rejected
      // version (instant clash, bad line…) leaves the norm fully untouched.
      const headerChanged = Boolean(
        deploymentNormId &&
          baseHeader &&
          (normCode.trim() !== (baseHeader.normCode || "") ||
            (description.trim() || "") !== (baseHeader.description || ""))
      );

      const payload: SaveDeploymentNormVersionPayload = {
        ...(deploymentNormId ? { deploymentNormId } : {}),
        projectId,
        normCode: normCode.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        effectiveFrom: effectiveFrom.toISOString(),
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
        // Numbers only, nulls stripped — a blank factor means "use the
        // default at compute time" and must not travel as 0.
        lines: lines.map((line): DeploymentNormLinePayload => {
          const out: DeploymentNormLinePayload = { assetId: Number(line.assetId) };
          const npf = num(line.nonProductionHoursFactor);
          const euf = num(line.effectiveUtilizationFactor);
          const af = num(line.applicationFactor);
          const uom = num(line.uomId);
          const prod = num(line.prodNorm);
          if (npf !== null) out.nonProductionHoursFactor = npf;
          if (euf !== null) out.effectiveUtilizationFactor = euf;
          if (af !== null) out.applicationFactor = af;
          if (uom !== null) out.uomId = uom;
          if (prod !== null) out.prodNorm = prod;
          return out;
        }),
      };

      const res = await deploymentNormApis.saveVersion(payload);
      if (!res?.status) {
        Swal.fire({
          icon: "error",
          title: "Could not save",
          text: res?.data?.message || res?.message || "Please try again.",
        });
        return;
      }

      if (headerChanged && deploymentNormId) {
        const headerRes = await deploymentNormApis.updateHeader({
          deploymentNormId,
          normCode: normCode.trim(),
          // Empty string is a deliberate CLEAR — undefined would be dropped in
          // transit and the old description would silently survive.
          description: description.trim(),
        });
        if (!headerRes?.status) {
          setSaveOpen(false);
          Swal.fire({
            icon: "warning",
            title: "Version saved, but the rename failed",
            text:
              headerRes?.data?.message ||
              headerRes?.message ||
              "The matrix version was saved; the Norm Code / description change was not.",
          });
          onSaved();
          return;
        }
      }

      setSaveOpen(false);
      Swal.fire({
        icon: "success",
        title: versionCount ? "New version saved" : "Equipment norm created",
        text: res?.data?.message || res?.message,
        timer: 2200,
        showConfirmButton: false,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const isInactive = baseHeader?.status === "Inactive";
  const saveLabel = versionCount ? "Save as New Version" : "Create First Version";

  const columns: any[] = [
    {
      title: "Equipment",
      key: "asset",
      width: 320,
      render: (_: any, row: LineRow) => (
        <Select
          showSearch
          value={row.assetId ?? undefined}
          placeholder="Pick a machine…"
          options={optionsForRow(row)}
          onChange={(value) => setLine(row._key, { assetId: Number(value) })}
          filterOption={(input, option: any) =>
            String(option?.searchText || "").includes(input.toLowerCase())
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Non Production Hours — extra non-productive time, entered as a PERCENT (5 = 5%). Blank = 0%. Stored as a fraction.">
          <span style={{ cursor: "help" }}>NPF %</span>
        </Tooltip>
      ),
      key: "npf",
      width: 110,
      render: (_: any, row: LineRow) => (
        <InputNumber
          value={toPct(row.nonProductionHoursFactor)}
          onChange={(v) =>
            setLine(row._key, {
              nonProductionHoursFactor: fromPct(v as number | null),
            })
          }
          min={0}
          step={1}
          placeholder="0"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Effective Utilization — entered as a PERCENT (100 = fully utilized). Blank = 100%. Must be greater than 0 (the hours formula divides by it). Stored as a fraction.">
          <span style={{ cursor: "help" }}>EUF %</span>
        </Tooltip>
      ),
      key: "euf",
      width: 110,
      render: (_: any, row: LineRow) => {
        const euf = num(row.effectiveUtilizationFactor);
        return (
          <InputNumber
            value={toPct(row.effectiveUtilizationFactor)}
            onChange={(v) =>
              setLine(row._key, {
                effectiveUtilizationFactor: fromPct(v as number | null),
              })
            }
            min={0}
            step={1}
            placeholder="100"
            status={euf !== null && euf <= 0 ? "error" : undefined}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: (
        <Tooltip title="Application Factor — how much of the quantity this machine applies to, entered as a PERCENT (100 = all of it). Blank = 100%. Stored as a fraction.">
          <span style={{ cursor: "help" }}>AF %</span>
        </Tooltip>
      ),
      key: "af",
      width: 110,
      render: (_: any, row: LineRow) => (
        <InputNumber
          value={toPct(row.applicationFactor)}
          onChange={(v) =>
            setLine(row._key, { applicationFactor: fromPct(v as number | null) })
          }
          min={0}
          step={1}
          placeholder="100"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Unit",
      key: "uom",
      width: 150,
      render: (_: any, row: LineRow) => (
        <Select
          value={row.uomId ?? undefined}
          placeholder="Cum/Hr"
          // Dropdown shows "Cubic Meter (Cum)"; the picked value reads as the
          // production-norm unit it really is: "Cubic Meter (Cum)/Hr".
          options={uomOptions.map((opt) => ({
            ...opt,
            hourLabel: `${opt.label}/Hr`,
          }))}
          optionLabelProp="hourLabel"
          onChange={(value) => setLine(row._key, { uomId: Number(value) })}
          showSearch
          filterOption={(input, option: any) =>
            String(option?.label || "").toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Output units per hour. 0 keeps the machine in the matrix as considered-but-not-deployed.">
          <span style={{ cursor: "help" }}>Prod. Norm</span>
        </Tooltip>
      ),
      key: "prodNorm",
      width: 120,
      render: (_: any, row: LineRow) => (
        <InputNumber
          value={row.prodNorm}
          onChange={(v) => setLine(row._key, { prodNorm: v as number | null })}
          min={0}
          placeholder="0"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      // DERIVED, display-only: the backend owns the formula
      // (computeTotalWorkingHours); this mirrors it for the what-if qty.
      title: `Hours for ${whatIfQty ?? "—"}`,
      key: "hours",
      width: 130,
      align: "right",
      render: (_: any, row: LineRow) => {
        const prod = num(row.prodNorm);
        if (!prod || prod <= 0) {
          return (
            <Tooltip title="Considered, not deployed — a zero production norm keeps the machine in the matrix but earns no hours.">
              <span className="text-soft" style={{ cursor: "help" }}>
                —
              </span>
            </Tooltip>
          );
        }
        const hours = computeDeploymentHours(whatIfQty, row);
        return (
          <span
            style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}
          >
            {formatHours(hours)}
          </span>
        );
      },
    },
    {
      // The machine's cost, in whichever basis the toggle is on. ₹/hr comes
      // straight from the asset's rate; ₹/unit is that rate times the LIVE
      // hoursPerUnit, recomputed as the user types — the server's copy goes
      // stale the moment a factor or prodNorm changes.
      title: `Cost ${rateSuffix}`,
      key: "cost",
      width: 150,
      align: "right",
      render: (_: any, row: LineRow) => {
        const rate = rateByAsset.get(Number(row.assetId));
        if (rate === undefined || rate === null) {
          return (
            <Tooltip title="This machine has no rate version in force. Set one under the Equipment Rates tab.">
              <span className="text-soft" style={{ cursor: "help" }}>
                no rate
              </span>
            </Tooltip>
          );
        }
        if (!showPerUnit) {
          return (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {money(rate)}
            </span>
          );
        }
        const hoursPerUnit = computeHoursPerUnit(row);
        if (hoursPerUnit === null) {
          return <span className="text-soft">—</span>;
        }
        const unit = uomOptions.find(
          (opt) => Number(opt.value) === Number(row.uomId)
        );
        const unitLabel = String(unit?.label || "unit").split(" (")[0];
        return (
          <Tooltip
            title={`${money(rate)} ₹/hr × ${hoursPerUnit.toFixed(
              4
            )} hr per ${unitLabel}`}
          >
            <span
              style={{ fontVariantNumeric: "tabular-nums", cursor: "help" }}
            >
              {moneyPerUnit(rate * hoursPerUnit)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "remove",
      width: 70,
      render: (_: any, row: LineRow) => (
        <a
          href="#remove"
          onClick={(e) => {
            e.preventDefault();
            removeLine(row._key);
          }}
          style={{ color: "#e85347", fontSize: 12 }}
        >
          Remove
        </a>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spin />
      </div>
    );
  }

  return (
    <div className="card-inner card-inner-lg">
      {/* Rows whose Prod. Norm is 0/blank are in the matrix but earn nothing —
          mute them so the deployed machines stand out. */}
      <style>{`.deployment-line-muted td { opacity: 0.55; }`}</style>

      <div
        className="d-flex align-items-center justify-content-between mb-3 flex-wrap"
        style={{ gap: 8 }}
      >
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
          <h5 className="mb-0" style={{ color: "#022c57", fontWeight: 600 }}>
            {isCreate ? "New Equipment Norm" : normCode || "Equipment Norm"}
          </h5>
          {!isCreate && (
            <Tag color={isInactive ? "red" : "green"}>
              {baseHeader?.status || "Active"}
            </Tag>
          )}
          <Tag color={projectId > 0 ? "blue" : "default"}>
            {projectId > 0
              ? projectName || `Project ${projectId}`
              : "Organization (Master)"}
          </Tag>
          {!isCreate &&
            (inForceFrom ? (
              <span className="text-soft" style={{ fontSize: 12 }}>
                Matrix in force since{" "}
                {dayjs(inForceFrom).format("DD MMM YYYY HH:mm")} · {versionCount}{" "}
                version{versionCount === 1 ? "" : "s"}
              </span>
            ) : (
              <Tag color="gold">No version yet</Tag>
            ))}
        </div>
        <Button color="light" size="sm" onClick={onClose}>
          <Icon name="arrow-left" />
          <span>Back</span>
        </Button>
      </div>

      {isInactive && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message="This norm is inactive"
          description="Inactive norms keep their version history but cannot take new versions. Activate it from the list first."
        />
      )}

      {/* ---- header fields ---- */}
      <div className="row mb-2">
        <div className="col-md-3 mb-2">
          <label className="form-label mb-1">Norm Code</label>
          <Input
            value={normCode}
            onChange={(e) => setNormCode(e.target.value)}
            placeholder="e.g. B3.1.1"
            maxLength={100}
          />
        </div>
        <div className="col-md-6 mb-2">
          <label className="form-label mb-1">Description</label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this norm covers (optional)"
            maxLength={255}
          />
        </div>
        <div className="col-md-3 mb-2">
          <label className="form-label mb-1">
            What-if Quantity{" "}
            <Tooltip title="Display only — never saved. Hours = (Qty × AF) / (Prod. Norm × EUF) × (1 + NPF), computed per line.">
              <span className="text-soft" style={{ cursor: "help" }}>
                ⓘ
              </span>
            </Tooltip>
          </label>
          <InputNumber
            value={whatIfQty}
            onChange={(v) => setWhatIfQty(v as number | null)}
            min={0}
            style={{ width: "100%" }}
            placeholder="1000"
          />
        </div>
      </div>

      {/* ---- the matrix ---- */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0" style={{ color: "#022c57" }}>
          Equipment Matrix
        </h6>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          {/* Always enabled here: the denominator is each line's own prodNorm,
              which every matrix already has — no data entry needed. */}
          <RateUnitToggle uomLabel={matrixUom} />
          <Button size="sm" color="light" outline className="btn-white" onClick={addLine}>
            <Icon name="plus" />
            <span>Add Equipment</span>
          </Button>
        </div>
      </div>

      <Table
        rowKey="_key"
        columns={columns}
        dataSource={lines}
        pagination={false}
        size="middle"
        bordered
        scroll={{ x: "max-content" }}
        rowClassName={(row: LineRow) => {
          const prod = num(row.prodNorm);
          return !prod || prod <= 0 ? "deployment-line-muted" : "";
        }}
        locale={{
          emptyText:
            "No equipment yet — Add Equipment to start this norm's matrix.",
        }}
      />

      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap" style={{ gap: 8 }}>
        <span className="text-soft" style={{ fontSize: 12 }}>
          Saving stores the WHOLE matrix as a new effective-dated version —
          blank factors default to 0 / 1 / 1 at compute time.
        </span>
        <Button
          color="primary"
          disabled={saving || isInactive}
          onClick={openSaveModal}
        >
          {saveLabel}
        </Button>
      </div>

      {/* ---- Save = INSERT a version: when it takes effect, and why ---- */}
      <Modal
        title={saveLabel}
        open={saveOpen}
        onCancel={() => setSaveOpen(false)}
        onOk={handleSaveVersion}
        okText={saving ? "Saving…" : "Save Version"}
        okButtonProps={{ disabled: saving }}
        destroyOnClose
      >
        <div className="text-soft mb-3" style={{ fontSize: 12.5 }}>
          {versionCount
            ? "Saving adds a NEW version of this norm's matrix — the current one is kept and stays in force for everything before the instant you pick. Back-dating recalculates history from that date."
            : "This becomes the norm's first version, in force from the instant you pick."}
        </div>
        <div className="mb-3">
          <label className="form-label mb-1">Effective from</label>
          <DatePicker
            value={effectiveFrom}
            onChange={(v) => setEffectiveFrom(v as Dayjs)}
            showTime={{ format: "HH:mm" }}
            format="DD MMM YYYY HH:mm"
            style={{ width: "100%" }}
            allowClear={false}
          />
        </div>
        <div>
          <label className="form-label mb-1">Remarks</label>
          <Input.TextArea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Why the deployment changed (e.g. revised BoQ, added rig)"
            maxLength={500}
            rows={2}
          />
        </div>
      </Modal>
    </div>
  );
};

export default DeploymentNormEditor;
